<template>
  <section v-if="visibleGuides.length" :class="compact ? 'mt-3 py-0' : 'mt-4 py-0'" aria-label="Tur rehberleri">
    <div
      class="rounded-xl border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-amber-50/60"
      :class="compact ? 'px-3 py-2.5' : 'p-3 sm:p-4'"
    >
    

      <div class="flex flex-wrap" :class="compact ? 'gap-2' : 'gap-2.5 sm:gap-3'">
          <div class="flex items-center gap-2" :class="compact ? 'mb-2' : 'mb-3'">
        
        <div class="min-w-0">
          <p class="mb-0 text-xs mt-1 font-bold text-slate-800">{{ heading }}:</p>
         </div>
       </div>
        <button
          v-for="guide in visibleGuides"
          :key="guide.profile.slug"
          type="button"
          class="flex min-w-0 items-center rounded-xl border border-white bg-white/90 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-amber-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
          :class="compact ? 'max-w-full gap-1.5 py-1 pl-1 pr-2' : 'gap-2 py-1.5 pl-1.5 pr-3'"
          @click="openGuide(guide.profile)"
        >
          <img
            v-if="guide.profile.image"
            :src="compact && guide.profile.thumbnail ? guide.profile.thumbnail : guide.profile.image"
            :alt="guide.name"
            :width="compact ? 28 : 44"
            :height="compact ? 28 : 44"
            class="shrink-0 rounded-lg border border-slate-100 object-cover bg-slate-100"
            :class="compact ? 'h-7 w-7' : 'h-10 w-10 sm:h-11 sm:w-11'"
            loading="lazy"
            @error="hideBrokenPhoto"
          />
          <span
            v-else
            class="inline-flex shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500"
            :class="compact ? 'h-7 w-7' : 'h-10 w-10 sm:h-11 sm:w-11'"
            aria-hidden="true"
          >
            <font-awesome-icon icon="fa-solid fa-user" :class="compact ? 'h-3 w-3' : 'h-4 w-4'" />
          </span>
          <div class="min-w-0">
            <p class="mb-0 truncate font-semibold leading-4 text-slate-700" :class="compact ? 'text-[11px]' : 'text-xs sm:text-sm'">
              {{ guide.name }}
            </p>
            <p v-if="!compact" class="mb-0 mt-0.5 line-clamp-1 text-[11px] leading-4 text-slate-500">
              {{ guide.profile.title }}
            </p>
          </div>
        </button>
      </div>
    </div>
  </section>

  <Teleport to="body">
    <div
      v-if="selectedGuide"
      class="fixed inset-0 z-[100] flex items-end bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:justify-center sm:p-5"
      @click.self="closeGuide"
    >
      <section
        role="dialog"
        aria-modal="true"
        :aria-labelledby="`guide-profile-${selectedGuide.slug}`"
        class="max-h-[90vh] w-full py-0 overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:max-w-xl sm:rounded-3xl"
      >
        <div class="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/95 px-5 py-4 backdrop-blur sm:px-6">
          <p class="mb-0 text-xs font-extrabold uppercase tracking-[.16em] text-teal-700">{{ isEnglish ? 'Your tour guide' : 'Tur rehberimiz' }}</p>
          <button type="button" class="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400" :aria-label="isEnglish ? 'Close guide profile' : 'Rehber profilini kapat'" @click="closeGuide">
            <font-awesome-icon icon="fa-solid fa-xmark" class="h-4 w-4" />
          </button>
        </div>

        <div class="p-5 sm:p-6">
          <div class="flex items-start gap-4">
            <img v-if="selectedGuide.image" :src="selectedGuide.image" :alt="selectedGuide.name" class="h-20 w-20 rounded-2xl object-cover shadow-sm sm:h-24 sm:w-24" />
            <div class="min-w-0 pt-1">
              <h2 :id="`guide-profile-${selectedGuide.slug}`" class="mb-1 text-xl font-black tracking-tight text-slate-900 sm:text-2xl">{{ selectedGuide.name }}</h2>
              <p class="mb-0 text-sm font-bold leading-5 text-teal-700">{{ selectedGuide.title }}</p>
            </div>
          </div>

          <p class="mb-0 mt-5 text-sm leading-6 text-slate-600">{{ selectedGuide.shortDescription }}</p>
          <div v-if="selectedGuide.biography?.length" class="mt-5 space-y-3 border-t border-slate-100 pt-5 text-sm leading-6 text-slate-600">
            <p v-for="paragraph in selectedGuide.biography" :key="paragraph" class="mb-0">{{ paragraph }}</p>
          </div>
          <div v-if="selectedGuide.expertise?.length" class="mt-5 flex flex-wrap gap-2">
            <span v-for="item in selectedGuide.expertise" :key="item" class="rounded-full bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-900">{{ item }}</span>
          </div>
          <a :href="`/rehberler/${selectedGuide.slug}`" class="mt-6 inline-flex items-center gap-2 rounded-xl bg-teal-700 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-teal-800" @click="closeGuide">
            {{ isEnglish ? 'View full profile' : 'Profilin tamamını incele' }}
            <font-awesome-icon icon="fa-solid fa-arrow-right" class="h-3 w-3" />
          </a>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref } from 'vue'
import { guides as guideProfiles } from '~/data/guides'

const props = defineProps({
  guides: { type: Array, default: () => [] },
  compact: { type: Boolean, default: false },
  locale: { type: String, default: 'tr' }
})

const normaliseGuideName = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase('tr-TR')
  .replace(/\b(dr|prof|doc|uzm|haci)\.?\b/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()

const guideProfileByName = new Map(guideProfiles.map((guide) => [normaliseGuideName(guide.name), guide]))
const selectedGuide = ref(null)

const visibleGuides = computed(() => (props.guides || [])
  .map((guide) => {
    const profile = guideProfileByName.get(normaliseGuideName(guide?.name))
    return profile ? { name: profile.name, profile } : null
  })
  .filter(Boolean))

const isEnglish = computed(() => String(props.locale || '').toLowerCase().startsWith('en'))
const heading = computed(() => isEnglish.value ? 'Your tour guides' : 'Tur rehberlerimiz')
const subheading = computed(() => isEnglish.value
  ? 'The team accompanying you on this journey'
  : 'Bu yolculukta size eşlik edecek ekip')

const openGuide = (guide) => {
  selectedGuide.value = guide
}

const closeGuide = () => {
  selectedGuide.value = null
}

const hideBrokenPhoto = (event) => {
  event.target.style.display = 'none'
}

</script>
